import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { getCheckoutStatus } from "@/lib/payments";
import { track, LOGO_URL, CREATIVE_QUESTIONNAIRE_URL } from "@/lib/ebook";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const POLL_INTERVAL_MS = 4000;
const MAX_ATTEMPTS = 30; // ~2 min — the Stripe proxy can take a while to expose paid sessions

const fmt = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [details, setDetails] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("missing");
      return;
    }
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          track("checkout_complete", "success-page", { amount: data.amount_total });
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        attemptsRef.current += 1;
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <div className="relative">
      <SEO title="Welcome To The Crowned Experience | Crowned Canvas Creations" description="Your Crowned Canvas Creations masterpiece officially begins here." path="/checkout/success" noindex />
      <Header />
      <main className="pt-32 md:pt-36 min-h-[80vh] flex items-start justify-center">
        <div className="max-w-2xl w-full mx-auto px-6 md:px-10 py-12">
          <div className="border border-white/10 bg-black/40 p-8 md:p-12 text-center">
            <img
              src={LOGO_URL}
              alt="Crowned Canvas Creations — official logo"
              className="h-24 md:h-28 w-auto object-contain mx-auto"
              data-testid="success-logo"
            />
            <StatusIcon status={status} />
            <StatusContent status={status} details={details} sessionId={sessionId} />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === "paid") return <CheckCircle2 className="w-12 h-12 text-[#D4AF37] mx-auto mt-6" strokeWidth={1.5} />;
  if (status === "polling") return <Loader2 className="w-12 h-12 text-[#D4AF37] mx-auto mt-6 animate-spin" strokeWidth={1.5} />;
  return <AlertCircle className="w-12 h-12 text-white/60 mx-auto mt-6" strokeWidth={1.5} />;
}

function StatusContent({ status, details, sessionId }) {
  if (status === "polling") {
    return (
      <>
        <p className="overline mt-6">Verifying Payment</p>
        <h1 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">Confirming with Stripe…</h1>
        <p className="text-white/60 mt-4 font-light">This usually takes a few seconds. Don&apos;t close the window.</p>
      </>
    );
  }

  if (status === "paid") {
    return (
      <>
        <h1 className="overline mt-6 !text-[13px] text-[#D4AF37]">Welcome To The Crowned Experience.</h1>
        <p className="font-serif-display text-3xl md:text-5xl mt-3 leading-[1.05]" data-testid="success-headline">
          Your masterpiece <span className="italic text-[#D4AF37]">officially begins here.</span>
        </p>

        {/* Order details */}
        <dl className="mt-8 border border-white/10 bg-[#111111] p-6 text-left text-sm divide-y divide-white/5" data-testid="order-summary">
          {details?.customer_name && (
            <div className="flex justify-between gap-4 py-2.5">
              <dt className="text-white/50">Customer</dt>
              <dd className="text-white/90 text-right">{details.customer_name}</dd>
            </div>
          )}
          <div className="flex justify-between gap-4 py-2.5">
            <dt className="text-white/50">Order number</dt>
            <dd className="text-white/90 font-mono text-xs self-center" data-testid="order-number">{details?.order_number || "—"}</dd>
          </div>
          <div className="flex justify-between gap-4 py-2.5">
            <dt className="text-white/50">Experience purchased</dt>
            <dd className="text-white/90 text-right">{details?.product_name || details?.metadata?.product_name || "—"}</dd>
          </div>
          <div className="flex justify-between gap-4 py-2.5">
            <dt className="text-white/50">Finished piece</dt>
            <dd className="text-white/90">16&Prime; &times; 20&Prime; Framed Canvas</dd>
          </div>
          <div className="flex justify-between gap-4 py-2.5">
            <dt className="text-white/50">Total experience price</dt>
            <dd className="text-white/90">{fmt(details?.total_price)}</dd>
          </div>
          <div className="flex justify-between gap-4 py-2.5">
            <dt className="text-white/50">Amount paid today</dt>
            <dd className="text-[#D4AF37] font-serif-display text-base">{fmt((details?.amount_total ?? 0) / 100)}</dd>
          </div>
          <div className="flex justify-between gap-4 py-2.5">
            <dt className="text-white/50">Remaining balance</dt>
            <dd className="text-white/90">{fmt(details?.remaining_balance)}</dd>
          </div>
          <div className="flex justify-between gap-4 py-2.5">
            <dt className="text-white/50">Next payment</dt>
            <dd className="text-white/70 text-xs text-right max-w-[240px]">{details?.balance_due || "Invoiced after final approval, before production."}</dd>
          </div>
        </dl>

        {/* Next step */}
        <div className="mt-8 border-2 border-[#D4AF37]/40 bg-[#824937]/15 p-6 text-left">
          <p className="overline text-[#D4AF37]">Next Step — Tell Us Your Story</p>
          <p className="font-serif-display text-2xl mt-3 leading-tight">Complete your Creative Questionnaire.</p>
          <p className="text-white/70 text-sm mt-2 font-light">
            This is the detailed questionnaire that tells our studio the story,
            vision and meaning behind your piece. Your artwork is built from it.
          </p>
          <a
            href={CREATIVE_QUESTIONNAIRE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("questionnaire_click", "success-page")}
            data-testid="success-questionnaire-btn"
            className="mt-5 inline-flex items-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all"
          >
            Complete Your Creative Questionnaire<ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Referral / share invitation — no monetary promises */}
        <div className="mt-8 border border-white/10 bg-[#111111] p-6 text-left" data-testid="referral-share-card">
          <p className="overline text-[#D4AF37]">Love What Crowned Is Building?</p>
          <p className="font-serif-display text-xl mt-3 leading-tight">
            Know someone whose story deserves to become a masterpiece?
          </p>
          <p className="text-white/60 text-sm mt-2 font-light">
            Share Crowned Canvas Creations with them.
          </p>
          <ShareRow />
        </div>

        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/contact" data-testid="success-contact" className="text-xs uppercase tracking-[0.22em] text-white/60 hover:text-[#D4AF37] transition-colors">
            Need help? Contact Crowned →
          </Link>
        </div>
      </>
    );
  }

  if (status === "expired") {
    return (
      <>
        <p className="overline mt-6 text-white/60">Session Expired</p>
        <h1 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">That session has expired.</h1>
        <p className="text-white/60 mt-4 font-light">No payment was taken. Restart from the experience page.</p>
        <Link to="/#experiences" className="mt-8 inline-flex items-center gap-2 border border-white/20 px-7 py-4 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all">
          Explore The Experiences
        </Link>
      </>
    );
  }
  if (status === "timeout") {
    return (
      <>
        <p className="overline mt-6 text-white/60">Still Processing</p>
        <h1 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">It&apos;s taking a little longer.</h1>
        <p className="text-white/60 mt-4 font-light">If you completed the payment, your confirmation email is on its way and your order is safe — payment status sometimes takes a few minutes to sync.</p>
        <Link to="/contact" data-testid="timeout-contact" className="mt-8 inline-flex items-center gap-2 border border-white/20 px-7 py-4 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all">
          Contact Crowned About My Order
        </Link>
        {sessionId && <p className="text-white/30 text-[10px] font-mono mt-6 break-all">Ref: {sessionId}</p>}
      </>
    );
  }
  if (status === "missing") {
    return (
      <>
        <p className="overline mt-6 text-white/60">No Session</p>
        <h1 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">We couldn&apos;t find your payment session.</h1>
        <Link to="/" className="mt-8 inline-flex items-center gap-2 border border-white/20 px-7 py-4 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all">
          Return Home
        </Link>
      </>
    );
  }
  return (
    <>
      <p className="overline mt-6 text-white/60">Something Went Wrong</p>
      <h1 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">We couldn&apos;t verify your payment.</h1>
      <p className="text-white/60 mt-4 font-light">If you completed the payment, you&apos;ll receive an email. Otherwise, please try again.</p>
      <Link to="/#experiences" className="mt-8 inline-flex items-center gap-2 bg-[#D4AF37] text-black px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all">
        Try Again
      </Link>
      {sessionId && <p className="text-white/30 text-[10px] font-mono mt-6 break-all">Ref: {sessionId}</p>}
    </>
  );
}

/** Post-purchase share row — invitation only, NO monetary/reward promises
 *  (referral credits & leaderboard are deferred to Phase 2). */
function ShareRow() {
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const text = "Crowned Canvas Creations — where your story becomes a masterpiece.";
  const fire = (channel) => track("referral_share_clicked", channel);

  const nativeShare = async () => {
    fire("native-share");
    if (navigator.share) {
      try { await navigator.share({ title: "Crowned Canvas Creations", text, url: shareUrl }); } catch (_) {}
    } else {
      try { await navigator.clipboard.writeText(shareUrl); } catch (_) {}
    }
  };
  const openShare = (channel, href) => {
    fire(channel);
    window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
  };
  const copy = async () => {
    fire("copy-link");
    try { await navigator.clipboard.writeText(shareUrl); } catch (_) {}
  };

  const btn = "inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-xs uppercase tracking-[0.18em] text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors";
  return (
    <div className="flex flex-wrap gap-2 mt-5">
      <button type="button" onClick={nativeShare} data-testid="share-native" className={btn}>Share Crowned</button>
      <button type="button" onClick={() => openShare("facebook", `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(text)}`)} data-testid="share-facebook" className={btn}>Facebook</button>
      <button type="button" onClick={() => openShare("twitter", `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`)} data-testid="share-twitter" className={btn}>X / Twitter</button>
      <button type="button" onClick={() => openShare("whatsapp", `https://api.whatsapp.com/send?text=${encodeURIComponent(text + " " + shareUrl)}`)} data-testid="share-whatsapp" className={btn}>WhatsApp</button>
      <button type="button" onClick={copy} data-testid="share-copy" className={btn}>Copy Link</button>
    </div>
  );
}
